import {
  createContext,
  type FC,
  type ReactNode,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { useAtom } from 'jotai';
import { CircularProgress, Box, Fade } from '@mui/material';
import { completedToursAtom } from '../../../atoms/tour-atoms';
import type { TourConfig, TourStep, TourState } from './tour.types';
import { findTourTarget, waitForElement, scrollIntoViewIfNeeded } from './tour.utils';
import TourOverlay from './TourOverlay';
import TourTooltip from './TourTooltip';

interface TourContextValue {
  startTour: (config: TourConfig, onComplete?: () => void) => void;
  endTour: () => void;
  isRunning: boolean;
}

export const TourContext = createContext<TourContextValue>({
  startTour: () => {},
  endTour: () => {},
  isRunning: false,
});

const BASE_Z_INDEX = 1200;
const BOOSTED_Z_INDEX = 1400;
const DEFAULT_PADDING = 8;
const SPOTLIGHT_SETTLE_MS = 50;

interface TourProviderProps {
  children: ReactNode;
}

const TourProvider: FC<TourProviderProps> = ({ children }) => {
  const [completedTours, setCompletedTours] = useAtom(completedToursAtom);
  const [tourState, setTourState] = useState<TourState>('idle');
  const [currentConfig, setCurrentConfig] = useState<TourConfig | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [isDetached, setIsDetached] = useState(false);
  const [imageExpanded, setImageExpanded] = useState(false);

  // Ref to avoid stale closures in async operations
  const activeRef = useRef(false);
  const configRef = useRef<TourConfig | null>(null);
  const stepIndexRef = useRef(0);
  const onCompleteRef = useRef<(() => void) | null>(null);
  const navigationDirectionRef = useRef<'forward' | 'backward'>('forward');

  const isRunning = tourState !== 'idle';
  const isLoading = tourState === 'executing-before-action' || tourState === 'waiting-for-element';

  const currentStep: TourStep | null =
    currentConfig && stepIndex < currentConfig.steps.length
      ? currentConfig.steps[stepIndex]
      : null;

  const overlayZIndex = currentStep?.zIndexBoost ? BOOSTED_Z_INDEX : BASE_Z_INDEX;
  const tooltipZIndex = overlayZIndex + 50;
  const spotlightPadding = currentStep?.spotlightPadding ?? DEFAULT_PADDING;

  /**
   * Clean up all open UI and reset tour state.
   */
  const cleanup = useCallback(async (config: TourConfig | null, idx: number) => {
    if (config && idx < config.steps.length) {
      const step = config.steps[idx];
      try {
        await step.onLeave?.();
      } catch {
        // Best-effort cleanup
      }
    }
  }, []);

  /**
   * Show the detached fallback tooltip for a step.
   */
  const showDetachedFallback = useCallback(async () => {
    setTargetEl(null);
    setIsDetached(true);
    setTourState('showing-tooltip');
    await new Promise((r) => setTimeout(r, 100));
    if (!activeRef.current) return;
    setTooltipVisible(true);
  }, []);

  /**
   * Skip to the next viable step in the current navigation direction,
   * or end the tour if no more steps remain.
   */
  const skipStep = useCallback(
    async (config: TourConfig, idx: number) => {
      const delta = navigationDirectionRef.current === 'backward' ? -1 : 1;
      const nextIdx = idx + delta;

      if (nextIdx >= 0 && nextIdx < config.steps.length) {
        stepIndexRef.current = nextIdx;
        setStepIndex(nextIdx);
        // Use setTimeout to avoid deep recursion
        setTimeout(() => {
          if (activeRef.current) showStepRef.current?.(config, nextIdx);
        }, 0);
      } else if (navigationDirectionRef.current === 'forward') {
        // Reached end of tour
        activeRef.current = false;
        setCompletedTours((prev) => ({ ...prev, [config.id]: true }));
        setTooltipVisible(false);
        setTargetEl(null);
        setCurrentConfig(null);
        setStepIndex(0);
        setTourState('idle');
        configRef.current = null;
        stepIndexRef.current = 0;
        onCompleteRef.current?.();
        onCompleteRef.current = null;
      }
      // If backward and nextIdx < 0, do nothing (stay on current step)
    },
    [setCompletedTours]
  );

  /**
   * End the tour -- runs cleanup on current step, marks complete, resets state.
   */
  const endTour = useCallback(async () => {
    const cfg = configRef.current;
    const idx = stepIndexRef.current;
    activeRef.current = false;

    // Clean up the current step
    await cleanup(cfg, idx);

    if (cfg) {
      setCompletedTours((prev) => ({ ...prev, [cfg.id]: true }));
    }

    setTooltipVisible(false);
    setImageExpanded(false);
    setTargetEl(null);
    setCurrentConfig(null);
    setStepIndex(0);
    setTourState('idle');
    configRef.current = null;
    stepIndexRef.current = 0;
    onCompleteRef.current?.();
    onCompleteRef.current = null;
  }, [cleanup, setCompletedTours]);

  // Use a ref for showStep so skipStep can call it without circular deps
  const showStepRef = useRef<((config: TourConfig, idx: number) => Promise<void>) | null>(null);

  /**
   * Show a specific step: run beforeAction, wait for element, position spotlight, show tooltip.
   */
  const showStep = useCallback(
    async (config: TourConfig, idx: number) => {
      if (!activeRef.current) return;

      const step = config.steps[idx];
      if (!step) {
        await endTour();
        return;
      }

      // --- Fast path: check if target already exists in DOM ---
      const existingEl = findTourTarget(step.target);

      // Check if fallback should be forced even when target exists
      const forceFallback = step.fallbackWhen?.() ?? false;
      if (forceFallback && (step.fallbackContent || step.fallbackImage)) {
        await showDetachedFallback();
        return;
      }

      // If target not found and there's no beforeAction that could create it,
      // immediately show fallback or skip (no waiting)
      if (!existingEl && !step.beforeAction) {
        if (step.fallbackContent || step.fallbackImage) {
          await showDetachedFallback();
        } else {
          await skipStep(config, idx);
        }
        return;
      }

      // If target already exists, no need for beforeAction or waitFor --
      // go straight to showing (unless step explicitly has beforeAction)
      if (existingEl && !step.beforeAction) {
        scrollIntoViewIfNeeded(existingEl);
        await new Promise((r) => setTimeout(r, SPOTLIGHT_SETTLE_MS));
        if (!activeRef.current) return;

        setTargetEl(existingEl);
        setIsDetached(false);
        setTourState('showing-tooltip');
        await new Promise((r) => setTimeout(r, 100));
        if (!activeRef.current) return;
        setTooltipVisible(true);
        return;
      }

      // --- Slow path: step has beforeAction, need to execute it ---
      setTourState('executing-before-action');

      if (step.beforeAction) {
        try {
          await step.beforeAction();
        } catch (err) {
          console.warn('Tour beforeAction failed:', err);
        }
        if (!activeRef.current) return;
      }

      // Wait for target or waitFor element (with reduced timeout since beforeAction already ran)
      setTourState('waiting-for-element');
      let el: HTMLElement | null = null;

      try {
        if (step.waitFor) {
          await waitForElement(step.waitFor, 2000);
        }
        el = findTourTarget(step.target);
        if (!el) {
          el = await waitForElement(step.target, 1500);
        }
      } catch {
        // Target not found after beforeAction
        console.warn(`Tour: could not find target "${step.target}" for step ${idx}`);
        if (!activeRef.current) return;

        if (step.fallbackContent || step.fallbackImage) {
          await showDetachedFallback();
        } else {
          await skipStep(config, idx);
        }
        return;
      }

      if (!activeRef.current) return;

      // Scroll into view if needed
      scrollIntoViewIfNeeded(el);

      // Brief pause for scroll/animation settle, then show
      await new Promise((r) => setTimeout(r, SPOTLIGHT_SETTLE_MS));
      if (!activeRef.current) return;

      setTargetEl(el);
      setIsDetached(false);
      setTourState('showing-tooltip');

      // Small delay to let spotlight animate before showing tooltip
      await new Promise((r) => setTimeout(r, 100));
      if (!activeRef.current) return;
      setTooltipVisible(true);
    },
    [endTour, showDetachedFallback, skipStep]
  );

  // Keep the ref up to date
  showStepRef.current = showStep;

  /**
   * Navigate to the next step.
   */
  const goNext = useCallback(async () => {
    if (!currentConfig || !activeRef.current) return;

    const cfg = currentConfig;
    const currentIdx = stepIndexRef.current;
    const nextIdx = currentIdx + 1;

    // Transitioning
    setTourState('transitioning');
    setTooltipVisible(false);
    setImageExpanded(false);

    // Cleanup current step
    await cleanup(cfg, currentIdx);
    if (!activeRef.current) return;

    if (nextIdx >= cfg.steps.length) {
      // Tour complete
      await endTour();
      return;
    }

    navigationDirectionRef.current = 'forward';
    stepIndexRef.current = nextIdx;
    setStepIndex(nextIdx);
    await showStep(cfg, nextIdx);
  }, [currentConfig, cleanup, endTour, showStep]);

  /**
   * Navigate to the previous step.
   */
  const goBack = useCallback(async () => {
    if (!currentConfig || !activeRef.current) return;

    const cfg = currentConfig;
    const currentIdx = stepIndexRef.current;
    const prevIdx = currentIdx - 1;

    if (prevIdx < 0) return;

    setTourState('transitioning');
    setTooltipVisible(false);
    setImageExpanded(false);

    // Cleanup current step
    await cleanup(cfg, currentIdx);
    if (!activeRef.current) return;

    navigationDirectionRef.current = 'backward';
    stepIndexRef.current = prevIdx;
    setStepIndex(prevIdx);
    await showStep(cfg, prevIdx);
  }, [currentConfig, cleanup, showStep]);

  /**
   * Start a tour from the beginning.
   */
  const startTour = useCallback(
    (config: TourConfig, onComplete?: () => void) => {
      if (activeRef.current) return; // Already running

      activeRef.current = true;
      configRef.current = config;
      stepIndexRef.current = 0;
      onCompleteRef.current = onComplete ?? null;
      navigationDirectionRef.current = 'forward';

      setCurrentConfig(config);
      setStepIndex(0);
      setTourState('executing-before-action');

      showStep(config, 0);
    },
    [showStep]
  );

  // Keyboard: Escape to skip
  useEffect(() => {
    if (!isRunning) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        endTour();
      } else if (e.key === 'ArrowRight') {
        goNext();
      } else if (e.key === 'ArrowLeft') {
        goBack();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isRunning, endTour, goNext, goBack]);

  // Handle advanceOnTargetClick
  useEffect(() => {
    if (!targetEl || !currentStep?.advanceOnTargetClick || tourState !== 'showing-tooltip') return;

    const handler = () => {
      goNext();
    };

    targetEl.addEventListener('click', handler);
    return () => targetEl.removeEventListener('click', handler);
  }, [targetEl, currentStep, tourState, goNext]);

  const contextValue: TourContextValue = {
    startTour,
    endTour,
    isRunning,
  };

  return (
    <TourContext.Provider value={contextValue}>
      {children}
      {isRunning &&
        createPortal(
          <>
            <TourOverlay
              targetEl={isDetached || imageExpanded ? null : targetEl}
              padding={spotlightPadding}
              zIndex={overlayZIndex}
              clickable={currentStep?.spotlightClickable ?? false}
              dimOnly={isDetached || isLoading || imageExpanded}
              onOverlayClick={() => {
                // Clicking the dark overlay area does nothing (prevents misclicks)
              }}
            />
            {isLoading && (
              <Fade in timeout={300}>
                <Box
                  sx={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: tooltipZIndex,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  data-cy="tour-loading"
                >
                  <CircularProgress size={40} sx={{ color: 'white' }} />
                </Box>
              </Fade>
            )}
            <TourTooltip
              anchorEl={isDetached || imageExpanded || currentStep?.centerTooltip ? null : targetEl}
              title={currentStep?.title ?? ''}
              content={
                isDetached && currentStep?.fallbackContent
                  ? currentStep.fallbackContent
                  : currentStep?.content ?? ''
              }
              image={currentStep?.fallbackImage}
              imageExpanded={imageExpanded}
              stepIndex={stepIndex}
              totalSteps={currentConfig?.steps.length ?? 0}
              placement={currentStep?.placement ?? 'bottom'}
              zIndex={tooltipZIndex}
              visible={tooltipVisible}
              detached={isDetached}
              centered={!isDetached && (!!currentStep?.centerTooltip || imageExpanded)}
              onNext={goNext}
              onBack={goBack}
              onSkip={endTour}
              onImageToggle={setImageExpanded}
            />
          </>,
          document.body
        )}
    </TourContext.Provider>
  );
};

export default TourProvider;
